import type {
    KartenDruckart,
    KartenEingabe,
    KartenFormat,
    MaterialArtikel,
    MaterialEmpfaengerAnzeige,
    MaterialMeldung,
    MaterialStatus,
} from './materialTypes';

/**
 * Regeln des Material-Bereichs (docs/materialverwaltung-plan.md, Client 4.) —
 * reine Funktionen ohne Supabase und ohne React. Die Komponenten unter
 * `components/material/bereich/` zeigen nur an, was hier entschieden wird.
 *
 * Zeiten werden in Europe/Berlin angezeigt, unabhängig von der Zeitzone des
 * Geräts; jede Funktion, die „heute“ braucht, bekommt `jetzt` übergeben.
 */

// ─── Grenzen (gleichlautend in supabase/material.sql, Nr. 178) ──────────────

export const ARTIKEL_NAME_MAX = 120;
export const ARTIKEL_LAGERORT_MAX = 120;
export const ARTIKEL_HINWEIS_MAX = 300;

/** So viele erledigte Meldungen zeigt der Verlauf; ältere bleiben in der Datenbank. */
export const VERLAUF_GRENZE = 200;

/** Zeichen wie Postgres `char_length` zählt (Codepunkte), nicht UTF-16-Einheiten. */
export const zeichenZahl = (s: string): number => Array.from(s).length;

// ─── Eingabeprüfung Artikel ─────────────────────────────────────────────────

export interface ArtikelEingabe {
    name: string;
    lagerort: string;
    hinweis: string;
}

/** Bereinigte Werte, so wie sie an `material_artikel_speichern` gehen. */
export interface ArtikelWerte {
    name: string;
    lagerort: string | null;
    hinweis: string | null;
}

export type ArtikelFeld = keyof ArtikelEingabe;

/**
 * `werte` ist genau dann gesetzt, wenn `fehler` leer ist. Kein
 * Diskriminator über `ok: true/false`: ohne `strict` im tsconfig engt
 * TypeScript Boolean-Literale nicht ein.
 */
export interface ArtikelPruefung {
    werte: ArtikelWerte | null;
    fehler: Partial<Record<ArtikelFeld, string>>;
}

/**
 * Getrimmt; leerer Lagerort/Hinweis wird null. Name 1–120, Lagerort ≤ 120,
 * Hinweis ≤ 300 Zeichen — dieselben CHECKs wie in der Tabelle, damit der
 * Dialog den Fehler am Feld zeigt statt einer Datenbankmeldung.
 */
export const pruefeArtikelEingabe = (e: ArtikelEingabe): ArtikelPruefung => {
    const name = (e.name ?? '').trim();
    const lagerort = (e.lagerort ?? '').trim();
    const hinweis = (e.hinweis ?? '').trim();
    const fehler: Partial<Record<ArtikelFeld, string>> = {};
    if (!name) fehler.name = 'Bitte einen Namen eingeben.';
    else if (zeichenZahl(name) > ARTIKEL_NAME_MAX) fehler.name = `Höchstens ${ARTIKEL_NAME_MAX} Zeichen.`;
    if (zeichenZahl(lagerort) > ARTIKEL_LAGERORT_MAX) fehler.lagerort = `Höchstens ${ARTIKEL_LAGERORT_MAX} Zeichen.`;
    if (zeichenZahl(hinweis) > ARTIKEL_HINWEIS_MAX) fehler.hinweis = `Höchstens ${ARTIKEL_HINWEIS_MAX} Zeichen.`;
    if (Object.keys(fehler).length) return { werte: null, fehler };
    return { werte: { name, lagerort: lagerort || null, hinweis: hinweis || null }, fehler: {} };
};

/** Eingabefelder aus einem bestehenden Artikel (Dialog „Bearbeiten“) bzw. leer. */
export const artikelEingabeAus = (a: MaterialArtikel | null): ArtikelEingabe => ({
    name: a?.name ?? '',
    lagerort: a?.lagerort ?? '',
    hinweis: a?.hinweis ?? '',
});

// ─── Status ─────────────────────────────────────────────────────────────────

/** Wohin ein Knopf den Status setzt. Zurück nach `offen` geht es nie. */
export type StatusZiel = Exclude<MaterialStatus, 'offen'>;

export const STATUS_TEXT: Record<MaterialStatus, string> = {
    offen: 'Offen',
    bestellt: 'Bestellt',
    erledigt: 'Aufgefüllt',
};

export const STATUS_KNOPF_TEXT: Record<StatusZiel, string> = {
    bestellt: 'Bestellt',
    erledigt: 'Aufgefüllt',
};

/**
 * Die Knöpfe je Status — dieselben Übergänge wie `material_status_setzen`
 * (Nr. 179): offen → bestellt, offen → erledigt, bestellt → erledigt.
 * `erledigt` ist endgültig.
 */
export const erlaubteStatusZiele = (status: MaterialStatus): StatusZiel[] => {
    if (status === 'offen') return ['bestellt', 'erledigt'];
    if (status === 'bestellt') return ['erledigt'];
    return [];
};

/** `leer`: kein offener Vorgang (Chip „—“). Die Farbe wählt die Komponente. */
export type ChipTon = MaterialStatus | 'leer';

export interface StatusChip {
    text: string;
    ton: ChipTon;
}

export const statusChip = (status: MaterialStatus | null | undefined): StatusChip =>
    status ? { text: STATUS_TEXT[status], ton: status } : { text: '—', ton: 'leer' };

// ─── Sortieren und Teilen ───────────────────────────────────────────────────

const zeitWert = (iso: string | null | undefined): number => zeitMs(iso) ?? Number.NEGATIVE_INFINITY;

const nachId = (a: { id: string }, b: { id: string }): number => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

const STATUS_RANG: Record<MaterialStatus, number> = { offen: 0, bestellt: 1, erledigt: 2 };

/**
 * Offene Vorgänge: erst die unbearbeiteten (`offen`), dann die bestellten;
 * darin die jüngste Auslösung zuerst — eine Erinnerung nach Ablauf der
 * Sperre rückt die Meldung damit wieder nach oben.
 */
export const sortiereOffene = (meldungen: readonly MaterialMeldung[]): MaterialMeldung[] =>
    [...meldungen].sort((a, b) =>
        STATUS_RANG[a.status] - STATUS_RANG[b.status]
        || zeitWert(b.zuletztAusgeloestAm) - zeitWert(a.zuletztAusgeloestAm)
        || nachId(a, b));

/** Verlauf: zuletzt aufgefüllt zuerst; ohne Datum ans Ende. */
export const sortiereVerlauf = (meldungen: readonly MaterialMeldung[]): MaterialMeldung[] =>
    [...meldungen].sort((a, b) =>
        zeitWert(b.erledigtAm ?? b.gemeldetAm) - zeitWert(a.erledigtAm ?? a.gemeldetAm) || nachId(a, b));

/** Offene oben (nicht `erledigt`), darunter der Verlauf — beide sortiert. */
export const teileMeldungen = (meldungen: readonly MaterialMeldung[]): { offen: MaterialMeldung[]; verlauf: MaterialMeldung[] } => ({
    offen: sortiereOffene(meldungen.filter(m => m.status !== 'erledigt')),
    verlauf: sortiereVerlauf(meldungen.filter(m => m.status === 'erledigt')),
});

/** Zahl für das Badge im Menü (`onOffeneGeaendert`). */
export const offeneAnzahl = (meldungen: readonly MaterialMeldung[]): number =>
    meldungen.filter(m => m.status !== 'erledigt').length;

/** Je Artikel die offene Meldung (höchstens eine, Unique-Index Nr. 178). */
export const offeneJeArtikel = (meldungen: readonly MaterialMeldung[]): Map<string, MaterialMeldung> => {
    const karte = new Map<string, MaterialMeldung>();
    for (const m of sortiereOffene(meldungen.filter(x => x.status !== 'erledigt'))) {
        if (!karte.has(m.artikelId)) karte.set(m.artikelId, m);
    }
    return karte;
};

const namensVergleich = new Intl.Collator('de', { sensitivity: 'base', numeric: true });

/** Aktive zuerst, darin nach Name (deutsch, Zahlen natürlich: „Papier A3“ vor „Papier A10“). */
export const sortiereArtikel = (artikel: readonly MaterialArtikel[]): MaterialArtikel[] =>
    [...artikel].sort((a, b) =>
        Number(b.aktiv) - Number(a.aktiv) || namensVergleich.compare(a.name, b.name) || nachId(a, b));

/** Ersetzt den Eintrag mit derselben `id` oder hängt ihn an (Antwort einer RPC in die Liste). */
export const ersetzeOderFuegeAn = <T extends { id: string }>(liste: readonly T[], neu: T): T[] => {
    const i = liste.findIndex(x => x.id === neu.id);
    if (i < 0) return [...liste, neu];
    const kopie = [...liste];
    kopie[i] = neu;
    return kopie;
};

// ─── Reiter ─────────────────────────────────────────────────────────────────

export type MaterialReiter = 'meldungen' | 'artikel';

/** Meldungen sieht jeder mit Zugang; Artikel nur, wer verwalten darf. */
export const sichtbareReiter = (darfBeschaffen: boolean, darfVerwalten: boolean): MaterialReiter[] => {
    const reiter: MaterialReiter[] = [];
    if (darfBeschaffen || darfVerwalten) reiter.push('meldungen');
    if (darfVerwalten) reiter.push('artikel');
    return reiter;
};

/** Der gemerkte Reiter, falls er sichtbar ist — sonst der erste sichtbare. */
export const gueltigerReiter = (gewaehlt: unknown, sichtbar: readonly MaterialReiter[]): MaterialReiter | null =>
    sichtbar.find(r => r === gewaehlt) ?? sichtbar[0] ?? null;

// ─── Zeit (Europe/Berlin) ───────────────────────────────────────────────────

const BERLIN = 'Europe/Berlin';
const TAG_MS = 86_400_000;

const berlinTeile = new Intl.DateTimeFormat('en-CA', {
    timeZone: BERLIN, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
});

interface BerlinZeit { jahr: number; monat: number; tag: number; stunde: number; minute: number }

const berlinZeit = (ms: number): BerlinZeit => {
    const t: Record<string, number> = {};
    for (const p of berlinTeile.formatToParts(new Date(ms))) if (p.type !== 'literal') t[p.type] = Number(p.value);
    return { jahr: t.year, monat: t.month, tag: t.day, stunde: t.hour % 24, minute: t.minute };
};

const WOCHENTAGE = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const zwei = (n: number): string => String(n).padStart(2, '0');
const kalenderTag = (z: BerlinZeit): number => Date.UTC(z.jahr, z.monat - 1, z.tag);

/** ISO-Zeitpunkt → Millisekunden; unlesbar oder leer → null. */
export const zeitMs = (iso: string | null | undefined): number | null => {
    if (!iso) return null;
    const t = Date.parse(iso.trim().replace(' ', 'T'));
    return Number.isFinite(t) ? t : null;
};

/**
 * „Mo 26.10., 09:14“ — im laufenden Jahr ohne Jahreszahl, sonst
 * „Mo 26.10.2025, 09:14“. Unlesbares wird unverändert gezeigt.
 */
export const zeitpunktText = (iso: string | null | undefined, jetzt: Date = new Date()): string => {
    const t = zeitMs(iso);
    if (t === null) return iso ?? '';
    const z = berlinZeit(t);
    const heute = berlinZeit(jetzt.getTime());
    const wochentag = WOCHENTAGE[new Date(kalenderTag(z)).getUTCDay()];
    const jahr = z.jahr === heute.jahr ? '' : String(z.jahr);
    return `${wochentag} ${zwei(z.tag)}.${zwei(z.monat)}.${jahr}, ${zwei(z.stunde)}:${zwei(z.minute)}`;
};

/** Kalendertage in Berlin zwischen `iso` und `jetzt` (gestern 23:59 → 1). */
export const tageSeit = (iso: string | null | undefined, jetzt: Date = new Date()): number | null => {
    const t = zeitMs(iso);
    if (t === null) return null;
    return Math.round((kalenderTag(berlinZeit(jetzt.getTime())) - kalenderTag(berlinZeit(t))) / TAG_MS);
};

/** „heute“, „vor 1 Tag“, „vor 5 Tagen“; ein Zeitpunkt in der Zukunft (Uhr des Geräts) gilt als heute. */
export const vorTagenText = (iso: string | null | undefined, jetzt: Date = new Date()): string => {
    const tage = tageSeit(iso, jetzt);
    if (tage === null) return '';
    if (tage <= 0) return 'heute';
    return tage === 1 ? 'vor 1 Tag' : `vor ${tage} Tagen`;
};

// ─── Anzeige-Texte einer Meldung ────────────────────────────────────────────

/** Name des Melders; anonym ohne Namen → „ohne Namen“ (wie im Mailtext der Route). */
export const melderText = (m: Pick<MaterialMeldung, 'melderName'>): string => (m.melderName ?? '').trim() || 'ohne Namen';

/** „Max Mustermann · Mo 26.10., 09:14 · vor 1 Tag“ — die ERSTE Meldung. */
export const gemeldetText = (m: MaterialMeldung, jetzt: Date = new Date()): string =>
    [melderText(m), zeitpunktText(m.gemeldetAm, jetzt), vorTagenText(m.gemeldetAm, jetzt)].filter(Boolean).join(' · ');

/** „2× gemeldet“ ab der ersten Erinnerung, sonst null. */
export const anzahlText = (anzahl: number): string | null =>
    Number.isFinite(anzahl) && anzahl >= 2 ? `${Math.floor(anzahl)}× gemeldet` : null;

/** „2× gemeldet · zuletzt Di 27.10., 08:00“ oder null bei einer einzigen Auslösung. */
export const wiederholtText = (m: MaterialMeldung, jetzt: Date = new Date()): string | null => {
    const anzahl = anzahlText(m.anzahl);
    return anzahl ? `${anzahl} · zuletzt ${zeitpunktText(m.zuletztAusgeloestAm, jetzt)}` : null;
};

/** „Bestellt Di 27.10., 10:00“ bzw. „Aufgefüllt …“; null, solange der Schritt fehlt. */
export const bestelltText = (m: MaterialMeldung, jetzt: Date = new Date()): string | null =>
    m.bestelltAm ? `Bestellt ${zeitpunktText(m.bestelltAm, jetzt)}` : null;

export const aufgefuelltText = (m: MaterialMeldung, jetzt: Date = new Date()): string | null =>
    m.erledigtAm ? `Aufgefüllt ${zeitpunktText(m.erledigtAm, jetzt)}` : null;

/** Zweite Zeile im Verlauf: „Gemeldet Mo 26.10., 09:14 von Max M. · 2× gemeldet · bestellt …“. */
export const verlaufText = (m: MaterialMeldung, jetzt: Date = new Date()): string =>
    [
        `Gemeldet ${zeitpunktText(m.gemeldetAm, jetzt)} ${(m.melderName ?? '').trim() ? `von ${melderText(m)}` : '(ohne Namen)'}`,
        anzahlText(m.anzahl),
        m.bestelltAm ? `bestellt ${zeitpunktText(m.bestelltAm, jetzt)}` : null,
    ].filter(Boolean).join(' · ');

export type MailVermerkArt = 'zugestellt' | 'fehler' | 'ohne';

export interface MailVermerk {
    art: MailVermerkArt;
    text: string;
}

/**
 * Vermerk der LETZTEN Auslösung (`material_mail_vermerken`). Eine Erinnerung
 * setzt beide Spalten zurück; bis die Route vermerkt hat — oder wenn sie
 * dabei abgestürzt ist —, steht hier „Kein Mail-Vermerk“. Die Glocke ist
 * davon unabhängig.
 */
export const mailVermerk = (m: Pick<MaterialMeldung, 'mailGesendetAm' | 'mailFehler'>): MailVermerk => {
    const fehler = (m.mailFehler ?? '').trim();
    if (fehler) return { art: 'fehler', text: `Mail nicht zugestellt: ${fehler}` };
    if (m.mailGesendetAm) return { art: 'zugestellt', text: 'Mail zugestellt' };
    return { art: 'ohne', text: 'Kein Mail-Vermerk' };
};

// ─── Empfänger-Kopfzeile ────────────────────────────────────────────────────

export const ROLLEN_PFAD = 'Verwaltung → Benutzer & Rechte → Rollen & Rechte';

export interface EmpfaengerKopf {
    text: string;
    /** Gelb hervorheben: niemand trägt die Rolle Beschaffung. */
    warnung: boolean;
}

/** null = (noch) nicht bekannt — die Zeile entfällt dann. */
export const empfaengerKopfzeile = (a: MaterialEmpfaengerAnzeige | null | undefined): EmpfaengerKopf | null => {
    if (!a) return null;
    const namen = a.namen.map(n => n.trim()).filter(Boolean);
    if (a.quelle === 'rolle' && namen.length) {
        return { text: `Meldungen gehen an: ${namen.join(', ')}`, warnung: false };
    }
    if (a.quelle === 'admins' && namen.length) {
        return {
            text: `Niemand hat die Rolle Beschaffung – Meldungen gehen an alle Admins. Rolle vergeben unter ${ROLLEN_PFAD}.`,
            warnung: true,
        };
    }
    return {
        text: `Niemand hat die Rolle Beschaffung, und kein Admin ist erreichbar – Meldungen erreichen niemanden. Rolle vergeben unter ${ROLLEN_PFAD}.`,
        warnung: true,
    };
};

// ─── Karten drucken ─────────────────────────────────────────────────────────

export interface KartenWahl<T extends string> {
    wert: T;
    titel: string;
    erklaerung: string;
}

export const KARTEN_FORMAT_VORGABE: KartenFormat = 'a6';

export const KARTEN_FORMATE: KartenWahl<KartenFormat>[] = [
    { wert: 'a6', titel: 'A6', erklaerung: 'Postkartengröße (105 × 148 mm) — fürs Regal oder Fach.' },
    {
        wert: 'lesezeichen',
        titel: 'Lesezeichen',
        erklaerung: 'Schmaler Streifen (74 × 210 mm) — steckt im Stapel, Kopfband und Artikelname ragen oben heraus.',
    },
];

/** Betreiber 27.10.2026: die Wendekarte wird zum Falten gedruckt; Duplex ist die Zweitwahl. */
export const KARTEN_DRUCKART_VORGABE: KartenDruckart = 'falten';

export const KARTEN_DRUCKARTEN: KartenWahl<KartenDruckart>[] = [
    {
        wert: 'falten',
        titel: 'Zum Falten',
        erklaerung: 'Vorder- und Rückseite auf einer Seite, in der Mitte falten – kein Duplexdruck nötig, Karte ist doppelt so stabil.',
    },
    {
        wert: 'beidseitig',
        titel: 'Beidseitig (Duplexdrucker)',
        erklaerung: 'Seite 1 Vorderseiten, Seite 2 Rückseiten. Im Druckdialog beidseitig drucken (lange Kante), dann an den Schnittmarken schneiden.',
    },
];

/** Karten je A4-Bogen (Vertrag P → V): 4 beidseitig, 2 zum Falten. */
export const kartenJeBogen = (druckart: KartenDruckart): number => (druckart === 'falten' ? 2 : 4);

/** „3 Karten · 2 A4-Bögen“ für den Druckdialog. */
export const bogenText = (anzahl: number, druckart: KartenDruckart): string => {
    if (anzahl <= 0) return 'Keine Karte ausgewählt';
    const boegen = Math.ceil(anzahl / kartenJeBogen(druckart));
    const karten = anzahl === 1 ? '1 Karte' : `${anzahl} Karten`;
    const bogen = boegen === 1 ? '1 A4-Bogen' : `${boegen} A4-Bögen`;
    return druckart === 'beidseitig' ? `${karten} · ${bogen} (beidseitig)` : `${karten} · ${bogen}`;
};

/**
 * Die ausgewählten Artikel als Karten fürs PDF — nur aktive (eine
 * deaktivierte Karte meldet „ungültig“), in Listenreihenfolge.
 */
export const kartenEingaben = (artikel: readonly MaterialArtikel[], auswahl: ReadonlySet<string>): KartenEingabe[] =>
    sortiereArtikel(artikel)
        .filter(a => a.aktiv && auswahl.has(a.id))
        .map(a => ({ name: a.name, lagerort: a.lagerort, hinweis: a.hinweis, qrToken: a.qrToken }));

/** „Materialkarten-A6-falten-2026-10-27.pdf“ (Datum in Berlin). */
export const kartenDateiname = (format: KartenFormat, druckart: KartenDruckart, jetzt: Date = new Date()): string => {
    const z = berlinZeit(jetzt.getTime());
    const teil = format === 'a6' ? 'A6' : 'Lesezeichen';
    return `Materialkarten-${teil}-${druckart}-${z.jahr}-${zwei(z.monat)}-${zwei(z.tag)}.pdf`;
};

// ─── Auswahl ────────────────────────────────────────────────────────────────

export const auswahlUmschalten = (auswahl: ReadonlySet<string>, id: string): Set<string> => {
    const neu = new Set(auswahl);
    if (neu.has(id)) neu.delete(id);
    else neu.add(id);
    return neu;
};

/** Alle aktiven ausgewählt → leeren; sonst alle aktiven wählen. */
export const alleUmschalten = (auswahl: ReadonlySet<string>, artikel: readonly MaterialArtikel[]): Set<string> => {
    const aktive = artikel.filter(a => a.aktiv).map(a => a.id);
    const alle = aktive.length > 0 && aktive.every(id => auswahl.has(id));
    return alle ? new Set() : new Set(aktive);
};

/** Nach dem Neuladen: nur, was noch existiert und aktiv ist, bleibt gewählt. */
export const auswahlBereinigen = (auswahl: ReadonlySet<string>, artikel: readonly MaterialArtikel[]): Set<string> => {
    const aktiv = new Set(artikel.filter(a => a.aktiv).map(a => a.id));
    return new Set([...auswahl].filter(id => aktiv.has(id)));
};
